import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { formatCurrency, formatDateTime, statusBadgeClass, statusLabel } from '../utils/formatters';
import { ShoppingBag, Search, ChevronLeft, ChevronRight, Eye, X, Printer, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

function SaleRow({ sale, onView, onCancel, onPrint }) {
  return (
    <tr>
      <td>
        <span className="font-medium text-gray-900 dark:text-white">{sale.sale_number || sale.number || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDateTime(sale.date || sale.created_at)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{sale.customer?.name || 'Umum'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{sale.items?.length || 0} item</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(sale.subtotal)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(sale.tax)}</span>
      </td>
      <td>
        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(sale.total)}</span>
      </td>
      <td>
        <span className={`badge ${statusBadgeClass(sale.payment_status || sale.status)}`}>
          {statusLabel(sale.payment_status || sale.status)}
        </span>
      </td>
      <td>
        <span className="text-sm text-gray-500 dark:text-gray-400">{sale.user?.name || sale.created_by || '-'}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button onClick={() => onView(sale)} className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded">
            <Eye className="h-4 w-4" />
          </button>
          <button onClick={() => onPrint(sale)} className="p-1.5 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <Printer className="h-4 w-4" />
          </button>
          {sale.status !== 'cancelled' && (
            <button onClick={() => onCancel(sale)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function SalesHistoryPage() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', payment_status: '', date_from: '', date_to: '' });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  useEffect(() => {
    fetchSales();
  }, [pagination.page, filters]);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/sales', { params });
      if (res.data.success) {
        setSales(res.data.data?.sales || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (sale) => {
    if (!confirm(`Yakin batalkan penjualan ${sale.sale_number || sale.number}?`)) return;
    try {
      const res = await api.put(`/sales/${sale.id}/cancel`);
      if (res.data.success) {
        toast.success('Penjualan berhasil dibatalkan');
        fetchSales();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handlePrint = (sale) => {
    setSelectedSale(sale);
    setTimeout(() => window.print(), 100);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Riwayat Penjualan</h1>
          <p className="page-subtitle">Lihat semua transaksi penjualan</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari nomor penjualan, pelanggan..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchSales()}
              />
            </div>
            <div>
              <select className="input" value={filters.payment_status} onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}>
                <option value="">Semua Status Pembayaran</option>
                <option value="cash">Tunai</option>
                <option value="credit">Kredit</option>
                <option value="partial">Sebagian</option>
                <option value="paid">Lunas</option>
                <option value="cancelled">Dibatalkan</option>
              </select>
            </div>
            <div>
              <input type="date" className="input" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            </div>
            <div>
              <input type="date" className="input" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <button onClick={fetchSales} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', payment_status: '', date_from: '', date_to: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-16" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-16" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : sales.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada riwayat penjualan.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nomor Penjualan</th>
                    <th>Tanggal</th>
                    <th>Pelanggan</th>
                    <th>Items</th>
                    <th>Subtotal</th>
                    <th>Tax</th>
                    <th>Total</th>
                    <th>Status Bayar</th>
                    <th>Dibuat Oleh</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <SaleRow
                      key={sale.id}
                      sale={sale}
                      onView={() => { setSelectedSale(sale); setShowDetailsModal(true); }}
                      onCancel={handleCancel}
                      onPrint={handlePrint}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedSale && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex justify-between items-start">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Detail Penjualan</h3>
              <button onClick={() => { setShowDetailsModal(false); setSelectedSale(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label text-sm font-semibold">Nomor Penjualan:</label>
                  <p className="text-gray-900 dark:text-white">{selectedSale.sale_number || selectedSale.number}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Tanggal:</label>
                  <p className="text-gray-900 dark:text-white">{formatDateTime(selectedSale.date || selectedSale.created_at)}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Pelanggan:</label>
                  <p className="text-gray-900 dark:text-white">{selectedSale.customer?.name || 'Umum'}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Status Bayar:</label>
                  <span className={`badge ${statusBadgeClass(selectedSale.payment_status || selectedSale.status)}`}>
                    {statusLabel(selectedSale.payment_status || selectedSale.status)}
                  </span>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Metode Bayar:</label>
                  <p className="text-gray-900 dark:text-white">{selectedSale.payment_method || '-'}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Dibuat Oleh:</label>
                  <p className="text-gray-900 dark:text-white">{selectedSale.user?.name || selectedSale.created_by || '-'}</p>
                </div>
              </div>

              {selectedSale.items && selectedSale.items.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold mb-3">Item Penjualan</h4>
                  <div className="table-container">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Produk</th>
                          <th>SKU</th>
                          <th>Qty</th>
                          <th>Harga Satuan</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSale.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.product_name || item.product?.name}</td>
                            <td>{item.sku || item.product?.sku}</td>
                            <td>{item.qty}</td>
                            <td>{formatCurrency(item.unit_price || item.price)}</td>
                            <td>{formatCurrency(item.subtotal || (item.qty * (item.unit_price || item.price)))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{formatCurrency(selectedSale.subtotal)}</span>
                </div>
                {selectedSale.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Diskon</span>
                    <span className="text-danger-600">-{formatCurrency(selectedSale.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax</span>
                  <span>{formatCurrency(selectedSale.tax)}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold pt-1">
                  <span>Total</span>
                  <span className="text-primary-800">{formatCurrency(selectedSale.total)}</span>
                </div>
                {selectedSale.cash_amount && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tunai Diterima</span>
                      <span>{formatCurrency(selectedSale.cash_amount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Kembalian</span>
                      <span className="text-success-600">{formatCurrency(selectedSale.cash_change || 0)}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button onClick={() => { setShowDetailsModal(false); setSelectedSale(null); }} className="btn-secondary flex-1">Tutup</button>
                <button onClick={() => { setShowDetailsModal(false); handlePrint(selectedSale); }} className="btn-primary flex-1">
                  <Printer className="h-4 w-4 mr-2" /> Cetak Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}